import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Save, Trash2 } from 'lucide-react';
import { z } from 'zod';
import { Button, DateField, ErrorState, FormActions, FormField, FormGrid, Loading, NumberField, PageHeader, Panel, Select, TextField, Textarea, useConfirm, useToast } from '@/components';
import { useCurrentUser } from '@/auth/AuthProvider';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { ApiError, errorMessage } from '@/lib/api';
import { applyApiErrors } from '@/lib/form';
import { useCodeValues } from '@/features/masters/api';
import { BusinessOperatorSelect } from '@/features/masters/components/BusinessOperatorSelect';
import { VesselSelect } from '@/features/masters/components/VesselSelect';
import { useDeleteEquipment, useEquipment, useSaveEquipment, type EquipmentDto } from './api';

const schema = z.object({
  businessOperatorId: z.number({ required_error: '事業実施者を選択してください', invalid_type_error: '事業実施者を選択してください' }).int().positive('事業実施者を選択してください').nullable(),
  managementNo: z.number().int().min(1).nullable(),
  category: z.string().min(1, '機器区分を選択してください'),
  fiscalYear: z.string(),
  vesselId: z.number().nullable(),
  vesselName: z.string().max(50, '50文字以内で入力してください'),
  registrationNo: z.string().max(15, '15桁以内で入力してください'),
  tonnage: z.number().min(0).nullable(),
  nonParticipantVesselName: z.string().max(50, '50文字以内で入力してください'),
  ownerName: z.string().max(100),
  userName: z.string().max(100),
  identificationNo: z.string().max(30, '30文字以内で入力してください'),
  maker: z.string().max(100),
  modelNo: z.string().max(100),
  subsidyProgramName: z.string().max(100),
  totalCost: z.number().min(0).nullable(),
  subsidyAmount: z.number().min(0).nullable(),
  operatorShare: z.number().min(0).nullable(),
  usefulLifeYears: z.number().int().min(0).max(100).nullable(),
  disposalRestrictedUntil: z.string(),
  disposalApprovedOn: z.string(),
  disposalDetail: z.string().max(2000),
  licensedOn: z.string(),
  licenseExpiresOn: z.string(),
  lendingFrom: z.string(),
  lendingTo: z.string(),
  note: z.string().max(2000),
});
type Values = z.infer<typeof schema>;

const s = (v: string | null | undefined) => v ?? '';
function toForm(e: EquipmentDto | undefined, ownId: number | null, year: number): Values {
  return {
    businessOperatorId: e?.businessOperatorId ?? ownId,
    managementNo: e?.managementNo ?? null,
    category: e?.category ?? '',
    fiscalYear: e?.fiscalYear != null ? String(e.fiscalYear) : e ? '' : String(year),
    vesselId: e?.vesselId ?? null,
    vesselName: s(e?.vesselName),
    registrationNo: s(e?.registrationNo),
    tonnage: e?.tonnage ?? null,
    nonParticipantVesselName: s(e?.nonParticipantVesselName),
    ownerName: s(e?.ownerName),
    userName: s(e?.userName),
    identificationNo: s(e?.identificationNo),
    maker: s(e?.maker),
    modelNo: s(e?.modelNo),
    subsidyProgramName: s(e?.subsidyProgramName),
    totalCost: e?.totalCost ?? null,
    subsidyAmount: e?.subsidyAmount ?? null,
    operatorShare: e?.operatorShare ?? null,
    usefulLifeYears: e?.usefulLifeYears ?? null,
    disposalRestrictedUntil: s(e?.disposalRestrictedUntil),
    disposalApprovedOn: s(e?.disposalApprovedOn),
    disposalDetail: s(e?.disposalDetail),
    licensedOn: s(e?.licensedOn),
    licenseExpiresOn: s(e?.licenseExpiresOn),
    lendingFrom: s(e?.lendingFrom),
    lendingTo: s(e?.lendingTo),
    note: s(e?.note),
  };
}

/** S-81 機器管理 登録・編集（基金のみ） */
export function EquipmentFormPage() {
  const { id } = useParams();
  const user = useCurrentUser();
  const eqId = id ? Number(id) : null;
  const { data, isPending, error, refetch } = useEquipment(eqId);
  const codes = useCodeValues();
  if (user.organizationType !== '基金') return <ErrorState error={new ApiError(403, 'FORBIDDEN', 'この画面を操作する権限がありません。')} />;
  if (eqId != null) {
    if (isPending) return <Loading block />;
    if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  }
  if (codes.isPending) return <Loading block />;
  return <EquipmentForm key={eqId ?? 'new'} equipment={eqId != null ? data : undefined} />;
}

function EquipmentForm({ equipment }: { equipment?: EquipmentDto }) {
  const isNew = !equipment;
  const navigate = useNavigate();
  const { year, years, yearPath } = useFiscalYear();
  const toast = useToast();
  const confirm = useConfirm();
  const { data: codes } = useCodeValues();
  const save = useSaveEquipment(equipment?.id ?? null);
  const del = useDeleteEquipment();
  const defaults = useMemo(() => toForm(equipment, null, year), [equipment, year]);
  const {
    register,
    control,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<Values>({ resolver: zodResolver(schema), defaultValues: defaults });
  const operatorId = watch('businessOperatorId');
  const listPath = yearPath('equipment');

  const onSubmit = handleSubmit(
    async (v) => {
      try {
        const body = { ...v, fiscalYear: v.fiscalYear === '' ? null : Number(v.fiscalYear) };
        const r = await save.mutateAsync(body);
        toast.success(isNew ? '機器を登録しました' : '機器を更新しました', `管理番号 ${r.managementNo}`);
        navigate(yearPath(`equipment/${r.id}`));
      } catch (e) {
        if (!applyApiErrors(e, setError)) toast.error('保存できませんでした', errorMessage(e));
      }
    },
    () => toast.error('保存できませんでした', '入力内容に誤りがあります。赤字の項目を確認してください。'),
  );

  const onDelete = async () => {
    if (!equipment) return;
    if (!(await confirm({ title: '機器を削除しますか？', message: `管理番号 ${equipment.managementNo} を削除します。この操作は取り消せません。`, tone: 'danger', confirmLabel: '削除する' }))) return;
    try {
      await del.mutateAsync(equipment.id);
      toast.success('機器を削除しました');
      navigate(listPath);
    } catch (e) {
      toast.error('削除できませんでした', errorMessage(e));
    }
  };

  const onCancel = async () => {
    if (isDirty && !(await confirm({ title: '入力内容を破棄しますか？', message: '保存していない変更は失われます。', confirmLabel: '破棄して戻る', tone: 'danger' }))) return;
    navigate(equipment ? yearPath(`equipment/${equipment.id}`) : listPath);
  };

  return (
    <form onSubmit={onSubmit} noValidate>
      <PageHeader eyebrow="船舶・計画 ／ 機器管理" title={isNew ? '機器の新規登録' : '機器の編集'} description="機器管理番号は空欄のまま保存すると、事業実施者ごとの連番で自動採番されます。" />
      <Panel title="基本情報">
        <FormGrid columns={3}>
          <FormField label="事業実施者" required error={errors.businessOperatorId?.message}>
            <Controller
              control={control}
              name="businessOperatorId"
              render={({ field }) => (
                <BusinessOperatorSelect
                  fiscalYear={year}
                  value={field.value}
                  onChange={(v) => {
                    field.onChange(v);
                    setValue('vesselId', null);
                  }}
                  placeholder="選択してください"
                  invalid={!!errors.businessOperatorId}
                />
              )}
            />
          </FormField>
          <FormField label="機器管理番号" hint={isNew ? '空欄で自動採番' : undefined} error={errors.managementNo?.message}>
            <Controller control={control} name="managementNo" render={({ field }) => <NumberField {...field} width="sm" />} />
          </FormField>
          <FormField label="機器区分" required error={errors.category?.message}>
            <Select placeholder="選択してください" options={(codes?.equipmentCategories ?? []).map((v) => ({ value: v, label: v }))} {...register('category')} />
          </FormField>
          <FormField label="年度（設置・管理年度）">
            <Select placeholder="指定なし" options={years.map((y) => ({ value: y.year, label: y.label }))} {...register('fiscalYear')} />
          </FormField>
          <FormField label="識別番号" hint="MMSI・電話番号・識別信号など" error={errors.identificationNo?.message}>
            <TextField {...register('identificationNo')} />
          </FormField>
          <FormField label="メーカー">
            <TextField {...register('maker')} />
          </FormField>
          <FormField label="型番">
            <TextField {...register('modelNo')} />
          </FormField>
        </FormGrid>
      </Panel>

      <Panel title="船舶・使用者" description="参加船を選ぶと船名・漁船登録番号・トン数が補完されます。名簿外の船に貸与する場合は「参加していない船名」に入力します。">
        <FormGrid columns={3}>
          <FormField label="参加船" error={errors.vesselId?.message}>
            <Controller
              control={control}
              name="vesselId"
              render={({ field }) => (
                <VesselSelect
                  fiscalYear={year}
                  businessOperatorId={operatorId}
                  value={field.value}
                  onChange={(id, opt) => {
                    field.onChange(id);
                    if (opt) {
                      setValue('vesselName', opt.name);
                      setValue('registrationNo', opt.registrationNo);
                      setValue('tonnage', opt.tonnage);
                    }
                  }}
                  fallbackLabel={equipment?.vesselName ?? undefined}
                  width="full"
                />
              )}
            />
          </FormField>
          <FormField label="船名（表示用）" error={errors.vesselName?.message}>
            <TextField {...register('vesselName')} />
          </FormField>
          <FormField label="漁船登録番号" error={errors.registrationNo?.message}>
            <TextField {...register('registrationNo')} />
          </FormField>
          <FormField label="トン数">
            <Controller control={control} name="tonnage" render={({ field }) => <NumberField {...field} decimals={2} unit="トン" />} />
          </FormField>
          <FormField label="外国船システムに参加していない船名" error={errors.nonParticipantVesselName?.message}>
            <TextField {...register('nonParticipantVesselName')} />
          </FormField>
          <FormField label="所有者">
            <TextField {...register('ownerName')} />
          </FormField>
          <FormField label="使用者">
            <TextField {...register('userName')} />
          </FormField>
        </FormGrid>
      </Panel>

      <Panel title="助成・処分制限">
        <FormGrid columns={3}>
          <FormField label="助成事業名">
            <TextField {...register('subsidyProgramName')} />
          </FormField>
          <FormField label="総事業費">
            <Controller control={control} name="totalCost" render={({ field }) => <NumberField {...field} unit="円" width="md" />} />
          </FormField>
          <FormField label="助成金">
            <Controller control={control} name="subsidyAmount" render={({ field }) => <NumberField {...field} unit="円" width="md" />} />
          </FormField>
          <FormField label="事業実施者負担金">
            <Controller control={control} name="operatorShare" render={({ field }) => <NumberField {...field} unit="円" width="md" />} />
          </FormField>
          <FormField label="耐用年数">
            <Controller control={control} name="usefulLifeYears" render={({ field }) => <NumberField {...field} unit="年" width="sm" />} />
          </FormField>
          <span />
          <FormField label="処分制限年月日">
            <DateField showWareki {...register('disposalRestrictedUntil')} />
          </FormField>
          <FormField label="処分承認年月日">
            <DateField showWareki {...register('disposalApprovedOn')} />
          </FormField>
          <FormField label="処分内容">
            <TextField {...register('disposalDetail')} />
          </FormField>
        </FormGrid>
      </Panel>

      <Panel title="免許・貸出">
        <FormGrid columns={4}>
          <FormField label="免許の年月日" error={errors.licensedOn?.message}>
            <DateField {...register('licensedOn')} />
          </FormField>
          <FormField label="免許の有効期限" error={errors.licenseExpiresOn?.message}>
            <DateField {...register('licenseExpiresOn')} />
          </FormField>
          <FormField label="貸出開始日" error={errors.lendingFrom?.message}>
            <DateField {...register('lendingFrom')} />
          </FormField>
          <FormField label="貸出終了日" error={errors.lendingTo?.message}>
            <DateField {...register('lendingTo')} />
          </FormField>
        </FormGrid>
      </Panel>

      <Panel title="備考">
        <Textarea rows={3} showCount maxLength={2000} {...register('note')} />
      </Panel>

      <FormActions>
        {!isNew && (
          <Button variant="danger" icon={<Trash2 size={16} />} onClick={onDelete} loading={del.isPending} style={{ marginRight: 'auto' }}>
            削除
          </Button>
        )}
        <Button onClick={onCancel}>キャンセル</Button>
        <Button type="submit" variant="primary" icon={<Save size={16} />} loading={isSubmitting}>
          {isNew ? '登録する' : '保存する'}
        </Button>
      </FormActions>
    </form>
  );
}
